import Cookies from 'js-cookie';
import { DevsiteCookies, DevsiteOrgCookies, doNotRemoveOnLogout } from 'shared/models/cookies';
import DevsiteEnv from 'modules/core/DevsiteEnv';

export function clearAllCookies() {
  Object.values(DevsiteCookies)
    .filter((cookieKey) => !doNotRemoveOnLogout.includes(cookieKey))
    .forEach(removeCookie);

  const allCookies = Object.keys(Cookies.get());
  Object.values(DevsiteOrgCookies).forEach((cookieKey) => {
    allCookies.filter((key) => key.startsWith(`${cookieKey}-`)).forEach(removeCookie);
  });
}

export function setCookie(cookieKey: DevsiteCookies | string, value: string, expiration?: Date) {
  return Cookies.set(cookieKey as string, value, {
    path: '/',
    secure: DevsiteEnv.SECURE_COOKIE,
    domain: getCookieDomain(),
    sameSite: 'strict',
    expires: expiration,
  });
}

/**
 * This will get the last two labels in the current location, and set that as the domain.
 * Ex: A cookie set from developer.yadkrow.com will have the domain .workday.com
 */
export function setRootDomainCookie(cookieKey: DevsiteCookies | string, value: string, expiration?: Date) {
  return Cookies.set(cookieKey as string, value, {
    path: '/',
    secure: DevsiteEnv.SECURE_COOKIE,
    domain: getRootDomain(),
    sameSite: 'strict',
    expires: expiration,
  });
}

export function setOrgCookie(cookieKey: DevsiteOrgCookies, value: string, orgUuid: string, expiration?: Date) {
  return setCookie(`${cookieKey}-${orgUuid}`, value, expiration);
}

// Explicit method call for internal use only (never used in prod) to persist internal features cookie
// not setting sameSite treats the setting as Lax, using strict introduces bug https://jira2.workday.com/browse/WCPX-6431
export function setInternalFeaturesCookie(cookieKey: DevsiteCookies, value: string) {
  return Cookies.set(cookieKey as string, value, {
    path: '/',
    secure: DevsiteEnv.SECURE_COOKIE,
    domain: getCookieDomain(),
    sameSite: 'lax',
  });
}

function getCookieDomain() {
  return window.location.hostname;
}

function getRootDomain() {
  const domain = window.location.hostname;
  const lastDot = domain.lastIndexOf('.');
  const secondToLastDot = domain.lastIndexOf('.', lastDot - 1);
  return secondToLastDot !== -1 ? domain.slice(secondToLastDot) : domain;
}

export function getCookie(cookieKey: DevsiteCookies | string) {
  // library conflict in tests leads to the Cookies object being empty in unit tests
  return Cookies.get?.(cookieKey as string);
}

export function getOrgCookie(cookieKey: DevsiteOrgCookies, orgUuid: string) {
  return getCookie(`${cookieKey}-${orgUuid}`);
}

/** Clears domain-scoped Developer Site cookies and legacy host-only cookies. */
export function removeCookie(cookieKey: string) {
  // Domain-scoped cookies must be expired with the same scope used by setCookie.
  Cookies.remove(cookieKey, {
    path: '/',
    secure: DevsiteEnv.SECURE_COOKIE,
    domain: getCookieDomain(),
    sameSite: 'strict',
  });
  // Also clear host-only cookies created by older sessions or test clients.
  Cookies.remove(cookieKey);
}

export function removeOrgCookie(cookieKey: string, orgUuid: string) {
  removeCookie(`${cookieKey}-${orgUuid}`);
}

export default DevsiteCookies;
